import {
	faArrowLeft,
	faUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatPrice } from '@supply-mover-2-0/core';
import {
	storefrontProductPath,
	type PortalInvoiceDetailResponseDto,
	type PortalOrderDetailResponseDto,
} from '@supply-mover-2-0/shared';
import {
	ImagePlaceholder,
	ResilientImage,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import { PAGE_CONTENT } from '../lib/pageWidth';
import * as invoiceService from '../services/portalInvoiceService';
import * as orderService from '../services/portalOrderService';

/** A single order/invoice line, normalized across the two document shapes. */
interface ReceiptLine {
	fkItemId: number | null;
	description: string | null;
	manufacturer: string | null;
	unitOfMeasure: string | null;
	itemExternalId: string | null;
	primaryImageUrl: string | null;
	/** Lot(s) the line shipped from — invoice lines only, and only from ERPs that report them. */
	lotNumber: string | null;
	quantity: number | null;
	/** Unit price the customer PAID (historical), not the live catalog price. */
	unitPrice: string | null;
	lineTotal: string | null;
}

/**
 * Receipt-style detail for a single order/invoice line.
 *
 * Unlike the catalog ProductDetailPage, this shows the HISTORICAL record — the
 * item the customer bought, at the price they PAID — with NO live promo, stock,
 * favorite, or add-to-cart. It is the destination for order/invoice line clicks
 * in BOTH shopping and account-portal-only storefronts: it reads only from the
 * order/invoice document, never the catalog, so it always works regardless of
 * the catalog-browsing gate. Data comes from the parent doc already in the
 * React Query cache; a cold deep-link refetches it via the same query. In
 * shopping mode (catalog browsable) it offers an optional "View in catalog"
 * link to the live product page; that link is absent in account-only mode.
 */
export function LineReceiptPage({
	source,
}: {
	source: 'order' | 'invoice';
}): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id, lineId } = useParams<{ id: string; lineId: string }>();
	const { token } = usePortalAuth();
	const display = useDisplaySettings();
	const queryClient = useQueryClient();

	const docId = id != null ? Number(id) : null;
	const targetLineId = lineId != null ? Number(lineId) : null;
	const backPath =
		source === 'order' ? `/orders/${id ?? ''}` : `/invoices/${id ?? ''}`;

	// Back = POP when we arrived in-app (the previous entry IS the parent
	// document). Pushing `backPath` here duplicated the detail entry, so
	// the detail page's own back control then popped onto this line item
	// again — the detail↔line ping-pong. The push remains only as the
	// cold-deep-link fallback where there is no in-app history to pop.
	const handleBack = (): void => {
		const historyState = window.history.state as { idx?: number } | null;
		if (historyState?.idx != null && historyState.idx > 0) {
			navigate(-1);
		} else {
			navigate(backPath, { replace: true });
		}
	};
	const queryKey = source === 'order' ? ['order', docId] : ['invoice', docId];

	const { data, isLoading } = useQuery<
		PortalOrderDetailResponseDto | PortalInvoiceDetailResponseDto
	>({
		queryKey,
		queryFn: async () =>
			source === 'order'
				? orderService.getOrder(token as string, docId as number)
				: invoiceService.getInvoiceDetail(token as string, docId as number),
		enabled: token != null && docId != null,
	});

	const line = resolveLine(source, data, targetLineId);

	if (isLoading) {
		return (
			<div className={`px-4 py-6 ${PAGE_CONTENT}`}>
				<div className="animate-pulse space-y-4">
					<div className="h-8 w-32 bg-brand-input rounded" />
					<div className="h-64 bg-brand-input rounded-xl" />
				</div>
			</div>
		);
	}

	return (
		<div className={`px-4 py-6 ${PAGE_CONTENT}`}>
			<button
				type="button"
				onClick={handleBack}
				className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-primary mb-4"
			>
				<FontAwesomeIcon icon={faArrowLeft} />
				{source === 'order' ? t('Back to order') : t('Back to invoice')}
			</button>

			{line == null ? (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 text-center text-brand-muted">
					{t('This line item could not be found.')}
				</div>
			) : (
				<div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden md:flex">
					{/* PD-17144: when the brand hides item images the picture
					    panel goes and the details (flex-1) fill the card. */}
					{display.showItemImages && (
						<div className="md:w-2/5 bg-brand-input aspect-square flex items-center justify-center">
							{line.primaryImageUrl != null && line.primaryImageUrl !== '' ? (
								<ResilientImage
									src={line.primaryImageUrl}
									alt={t(line.description ?? '')}
									className="w-full h-full object-contain"
									onLikelyStaleUrl={() => {
										void queryClient.invalidateQueries({ queryKey });
									}}
									fallback={<ImagePlaceholder size="lg" />}
								/>
							) : (
								<ImagePlaceholder size="lg" />
							)}
						</div>
					)}

					<div className="flex-1 p-5 space-y-4">
						<h1 className="text-lg font-semibold text-default">
							{t(line.description ?? '')}
						</h1>

						<dl className="text-sm space-y-1.5">
							{line.manufacturer != null && line.manufacturer !== '' && (
								<ReceiptRow
									label={t('Manufacturer')}
									value={t(line.manufacturer)}
								/>
							)}
							{line.unitOfMeasure != null && line.unitOfMeasure !== '' && (
								<ReceiptRow
									label={t('Unit of Measure')}
									value={t(line.unitOfMeasure)}
								/>
							)}
							{line.itemExternalId != null && line.itemExternalId !== '' && (
								<ReceiptRow
									label={t('Item #')}
									value={t(line.itemExternalId)}
								/>
							)}
							{line.lotNumber != null && line.lotNumber !== '' && (
								<ReceiptRow label={t('Lot')} value={t(line.lotNumber)} />
							)}
							<ReceiptRow
								label={t('Quantity')}
								value={t(String(line.quantity ?? 0))}
							/>
							{/* PD-14239: hide the historical unit price paid and
							    line total when this portal user cannot see pricing. */}
							{display.canSeePricing && (
								<>
									<ReceiptRow
										label={t('Unit price paid')}
										value={t(formatPrice(line.unitPrice))}
									/>
									<ReceiptRow
										label={t('Line total')}
										value={t(formatCurrency(line.lineTotal) ?? '-')}
									/>
								</>
							)}
						</dl>

						{/* Shopping mode only: a way to reach the live catalog page on
						    demand. Absent in account-portal-only mode (gate false), and
						    for non-item lines (freight/surcharge) with no fkItemId. */}
						{display.canBrowseCatalog && line.fkItemId != null && (
							<Link
								to={storefrontProductPath(line.fkItemId)}
								className="inline-flex items-center gap-2 text-sm font-medium text-portal-accent hover:underline"
							>
								<FontAwesomeIcon
									icon={faUpRightFromSquare}
									className="text-xs"
								/>
								{t('View in catalog')}
							</Link>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

function ReceiptRow({
	label,
	value,
}: {
	label: string;
	value: string;
}): JSX.Element {
	return (
		<div className="flex justify-between gap-4">
			<dt className="text-brand-muted">{label}</dt>
			<dd className="text-default font-medium text-right">{value}</dd>
		</div>
	);
}

/** Find the target line in the cached parent doc and normalize the two shapes. */
function resolveLine(
	source: 'order' | 'invoice',
	data:
		| PortalOrderDetailResponseDto
		| PortalInvoiceDetailResponseDto
		| undefined,
	targetLineId: number | null,
): ReceiptLine | null {
	if (data == null || targetLineId == null) {
		return null;
	}

	if (source === 'order') {
		const l = (data as PortalOrderDetailResponseDto).data.lineItems.find(
			(x) => x.id === targetLineId,
		);
		if (l == null) {
			return null;
		}
		return {
			fkItemId: l.fkItemId,
			description: l.description,
			manufacturer: l.manufacturer,
			unitOfMeasure: l.unitOfMeasure,
			itemExternalId: l.itemExternalId,
			primaryImageUrl: l.primaryImageUrl,
			// Lots are allocated at fulfillment, so an order line never has one.
			lotNumber: null,
			quantity: l.quantityOrdered,
			unitPrice: l.price,
			lineTotal: l.subtotal,
		};
	}

	const l = (data as PortalInvoiceDetailResponseDto).data.lineItems.find(
		(x) => x.id === targetLineId,
	);
	if (l == null) {
		return null;
	}
	return {
		fkItemId: l.fkItemId,
		description: l.description,
		manufacturer: l.manufacturer,
		unitOfMeasure: l.unitOfMeasure,
		itemExternalId: l.itemExternalId,
		primaryImageUrl: l.primaryImageUrl,
		lotNumber: l.lotNumber,
		quantity: l.quantity,
		unitPrice: l.price,
		lineTotal: l.total,
	};
}
